import * as React from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import type { AdminForm } from './EditorTypes';
import {
  APPENDIX_SYNC_ACT_DAY_VAR,
  APPENDIX_SYNC_ACT_MONTH_VAR,
  APPENDIX_SYNC_ACT_NO_VAR,
  APPENDIX_SYNC_ACT_YEAR_VAR,
  APPENDIX_SYNC_BUILDER_VAR,
  APPENDIX_SYNC_OBJECT_VAR,
} from './appendixRegistryHelpers';
import type { ActTemplateVariableDto } from '../../services/ActTemplateVariablesService';
import { services } from '../../services';

interface AdminBlockEditorDialogProps {
  visible: boolean;
  adminForm: AdminForm | null;
  onHide: () => void;
  onDelete: () => void;
  onSave: () => void;
  onChange: <K extends keyof AdminForm>(key: K, value: AdminForm[K]) => void;
}

export const AdminBlockEditorDialog: React.FC<AdminBlockEditorDialogProps> = (props) => {
  const { visible, adminForm, onHide, onDelete, onSave, onChange } = props;
  const [templateVariables, setTemplateVariables] = React.useState<ActTemplateVariableDto[]>([]);
  const [variableListDialogVisible, setVariableListDialogVisible] = React.useState(false);
  const [variableFilter, setVariableFilter] = React.useState('');

  React.useEffect(() => {
    if (visible) {
      services.actTemplateVariablesService.Fetch()
        .then((res) => setTemplateVariables(Array.isArray(res?.data) ? res.data : []))
        .catch(() => setTemplateVariables([]));
    }
  }, [visible]);

  const onInsertVariable = React.useCallback(
    (variable: ActTemplateVariableDto) => {
      const current = adminForm?.value ?? '';
      onChange('value', current + `{{{${variable.Name}}}}`);
      setVariableListDialogVisible(false);
    },
    [adminForm?.value, onChange]
  );

  const filteredVariables = React.useMemo(() => {
    const q = (variableFilter || '').trim().toLowerCase();
    if (!q) return templateVariables;
    return templateVariables.filter(
      (v) =>
        (v.Name || '').toLowerCase().includes(q) ||
        (v.Description || '').toLowerCase().includes(q)
    );
  }, [templateVariables, variableFilter]);

  const footer = (
    <div className="d-flex justify-content-between align-items-center w-100" style={{ gap: '0.5rem' }}>
      <div className="d-flex" style={{ gap: '0.5rem' }}>
        <Button label="Удалить блок" icon="pi pi-trash" severity="danger" onClick={onDelete} />
      </div>
      <div className="d-flex" style={{ gap: '0.5rem' }}>
        <Button label="Сохранить" icon="pi pi-save" onClick={onSave} />
      </div>
    </div>
  );

  return (
    <Dialog
      header="Редактирование блока"
      visible={visible}
      style={{ width: '920px', maxWidth: '98vw' }}
      modal
      onHide={onHide}
      footer={footer}
    >
      <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '8px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {adminForm && (
            <div className="p-fluid grid" style={{ gap: '8px 12px' }}>
              <div className="col-12">
                <label style={{ fontWeight: 600, display: 'block', color: '#2563eb' }}>
                  Значение элемента
                  {adminForm.type === 'enum' && adminForm.enumText && (
                    <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#6b7280', marginLeft: '8px' }}>
                      (выберите из списка)
                    </span>
                  )}
                </label>
                {adminForm.type === 'enum' && adminForm.enumText ? (
                  <Dropdown
                    value={adminForm.value || ''}
                    options={[
                      { label: '(пусто)', value: '' },
                      ...adminForm.enumText.split('|').map(s => s.trim()).filter(Boolean).map(item => ({
                        label: item,
                        value: item
                      }))
                    ]}
                    onChange={(e) => onChange('value', e.value || '')}
                    className="w-100"
                    placeholder="Выберите значение"
                    style={{ border: '2px solid #2563eb', borderRadius: '6px' }}
                  />
                ) : (
                  <>
                    <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                      <InputText
                        value={adminForm.value || ''}
                        onChange={(e) => onChange('value', e.target.value)}
                        className="w-100"
                        placeholder="Введите значение элемента"
                        style={{ border: '2px solid #2563eb', borderRadius: '6px' }}
                      />
                      <Button
                        type="button"
                        icon="pi pi-list"
                        label="Переменные"
                        outlined
                        onClick={() => setVariableListDialogVisible(true)}
                        title="Вставить переменную из списка"
                      />
                    </div>
                    <Dialog
                      header="Выберите переменную для вставки"
                      visible={variableListDialogVisible}
                      style={{ width: '90%' }}
                      modal
                      onHide={() => {
                        setVariableListDialogVisible(false);
                        setVariableFilter('');
                      }}
                      dismissableMask
                    >
                      <div className="d-flex flex-column" style={{ gap: '8px' }}>
                        <InputText
                          value={variableFilter}
                          onChange={(e) => setVariableFilter(e.target.value)}
                          placeholder="Поиск по имени и описанию..."
                          className="w-100"
                        />
                        <DataTable
                          value={filteredVariables}
                          size="small"
                          scrollable
                          scrollHeight="320px"
                          emptyMessage={templateVariables.length === 0 ? 'Загрузка...' : 'Нет переменных по фильтру'}
                          onRowClick={(e) => {
                            const row = e.data as ActTemplateVariableDto;
                            if (row?.Name) onInsertVariable(row);
                          }}
                          rowClassName={() => 'cursor-pointer'}
                          tableStyle={{ width: '100%' }}
                        >
                          <Column field="Name" header="Переменная" style={{ textAlign: 'left', width: '180px' }} />
                          <Column field="Description" header="Описание" style={{ textAlign: 'left' }} />
                          <Column
                            body={(row: ActTemplateVariableDto) => (
                              <Button
                                type="button"
                                label="Вставить"
                                icon="pi pi-plus"
                                size="small"
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  onInsertVariable(row);
                                }}
                              />
                            )}
                            style={{ width: '100px', textAlign: 'right' }}
                          />
                        </DataTable>
                      </div>
                    </Dialog>
                  </>
                )}
              </div>
              <div className="col-12" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '12px', marginTop: '8px' }}>
                <label style={{ fontWeight: 600, display: 'block' }}>Ключ поля (data-var)</label>
                <InputText value={adminForm.key} onChange={(e) => onChange('key', e.target.value)} className="w-100" />
              </div>
              <div className="col-12">
                <label style={{ fontWeight: 600, display: 'block' }}>Описание (data-label)</label>
                <InputText value={adminForm.label} onChange={(e) => onChange('label', e.target.value)} className="w-100" />
              </div>
              <div className="col-12">
                <label style={{ fontWeight: 600, display: 'block' }}>Подсказка (data-tooltip)</label>
                <InputText value={adminForm.tooltip || ''} onChange={(e) => onChange('tooltip', e.target.value)} className="w-100" />
              </div>
              <div className="col-12">
                <label style={{ fontWeight: 600, display: 'block' }}>Тип (data-type)</label>
                <Dropdown
                  value={adminForm.type}
                  options={[
                    { label: 'Строка', value: 'string' },
                    { label: 'Число', value: 'number' },
                    { label: 'Список', value: 'enum' },
                  ]}
                  onChange={(e) => onChange('type', e.value)}
                  className="w-100"
                />
              </div>
              <div className="col-6">
                <label style={{ fontWeight: 600, display: 'block' }}>Обязательное (data-required)</label>
                <Checkbox checked={adminForm.required} onChange={(e) => onChange('required', !!e.checked)} />
              </div>
              <div className="col-6">
                <label style={{ fontWeight: 600, display: 'block' }}>Макс. длина (data-maxlen)</label>
                <InputText value={String(adminForm.maxLen ?? 0)} onChange={(e) => onChange('maxLen', Number(e.target.value || 0))} className="w-100" />
              </div>
              {adminForm.type === 'number' && (
                <>
                  <div className="col-4">
                    <label style={{ fontWeight: 600, display: 'block' }}>Min значение (data-min)</label>
                    <InputText value={adminForm.min ?? ''} onChange={(e) => onChange('min', e.target.value)} className="w-100" />
                  </div>
                  <div className="col-4">
                    <label style={{ fontWeight: 600, display: 'block' }}>Max значение (data-max)</label>
                    <InputText value={adminForm.max ?? ''} onChange={(e) => onChange('max', e.target.value)} className="w-100" />
                  </div>
                  <div className="col-4">
                    <label style={{ fontWeight: 600, display: 'block' }}>Десятичные (data-decimals)</label>
                    <InputText value={String(adminForm.decimals ?? 0)} onChange={(e) => onChange('decimals', Number(e.target.value || 0))} className="w-100" />
                  </div>
                </>
              )}
              {adminForm.type === 'enum' && (
                <div className="col-12">
                  <label style={{ fontWeight: 600, display: 'block' }}>Список значений (через |) (data-enum)</label>
                  <InputText value={adminForm.enumText || ''} onChange={(e) => onChange('enumText', e.target.value)} className="w-100" />
                </div>
              )}
              <div className="col-12">
                <label style={{ fontWeight: 600, display: 'block' }}>Отключить редактирование этого блока (data-editable)</label>
                <Checkbox checked={!!adminForm.disabled} onChange={(e) => onChange('disabled', !!e.checked)} />
              </div>
              <div className="col-12" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '12px', marginTop: '8px' }}>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: '8px' }}>
                  Реестр приложений № 1 (data-appendix-registry)
                </label>
                <div className="d-flex align-items-center mb-2" style={{ gap: '8px' }}>
                  <Checkbox
                    inputId="appendix-registry-enabled"
                    checked={!!adminForm.appendixRegistryEnabled}
                    onChange={(e) => onChange('appendixRegistryEnabled', !!e.checked)}
                  />
                  <label htmlFor="appendix-registry-enabled" style={{ cursor: 'pointer', marginBottom: 0 }}>
                    Поле открывает модалку таблицы реестра (только для акта АОСР)
                  </label>
                </div>
                {adminForm.appendixRegistryEnabled && (
                  <>
                    <div className="col-12 px-0 mb-2">
                      <label style={{ fontWeight: 600, display: 'block', fontSize: '12px' }}>
                        Идентификатор приложения (data-appendix-registry)
                      </label>
                      <InputText
                        value={adminForm.appendixRegistryAppendixId || '1'}
                        onChange={(e) => onChange('appendixRegistryAppendixId', e.target.value)}
                        className="w-100"
                        placeholder="1"
                      />
                    </div>
                    <div className="col-12 px-0 mb-2">
                      <label style={{ fontWeight: 600, display: 'block', fontSize: '12px' }}>
                        Объект капитального строительства ({APPENDIX_SYNC_OBJECT_VAR}) (data-var значение)
                      </label>
                      <InputText
                        value={adminForm.appendixSyncObjectVar || ''}
                        onChange={(e) => onChange('appendixSyncObjectVar', e.target.value)}
                        className="w-100"
                        placeholder="data-var ключ поля"
                      />
                    </div>
                    <div className="col-12 px-0 mb-2">
                      <label style={{ fontWeight: 600, display: 'block', fontSize: '12px' }}>
                        Представитель лица, осуществляющего строительство ({APPENDIX_SYNC_BUILDER_VAR}) (data-var значение)
                      </label>
                      <InputText
                        value={adminForm.appendixSyncBuilderVar || ''}
                        onChange={(e) => onChange('appendixSyncBuilderVar', e.target.value)}
                        className="w-100"
                        placeholder="data-var ключ поля"
                      />
                    </div>
                    <div className="col-12 px-0 mb-2">
                      <label style={{ fontWeight: 600, display: 'block', fontSize: '12px' }}>
                        № акта в заголовке приложения ({APPENDIX_SYNC_ACT_NO_VAR})
                      </label>
                      <InputText
                        value={adminForm.appendixSyncActNoVar || ''}
                        onChange={(e) => onChange('appendixSyncActNoVar', e.target.value)}
                        className="w-100"
                        placeholder="data-var ключ поля"
                      />
                    </div>
                    <div className="col-12 px-0 mb-2">
                      <label style={{ fontWeight: 600, display: 'block', fontSize: '12px' }}>
                        Число даты акта в заголовке ({APPENDIX_SYNC_ACT_DAY_VAR})
                      </label>
                      <InputText
                        value={adminForm.appendixSyncActDayVar || ''}
                        onChange={(e) => onChange('appendixSyncActDayVar', e.target.value)}
                        className="w-100"
                        placeholder="data-var ключ поля"
                      />
                    </div>
                    <div className="col-12 px-0 mb-2">
                      <label style={{ fontWeight: 600, display: 'block', fontSize: '12px' }}>
                        Месяц даты акта ({APPENDIX_SYNC_ACT_MONTH_VAR})
                      </label>
                      <InputText
                        value={adminForm.appendixSyncActMonthVar || ''}
                        onChange={(e) => onChange('appendixSyncActMonthVar', e.target.value)}
                        className="w-100"
                        placeholder="data-var ключ поля"
                      />
                    </div>
                    <div className="col-12 px-0 mb-2">
                      <label style={{ fontWeight: 600, display: 'block', fontSize: '12px' }}>
                        Год даты акта ({APPENDIX_SYNC_ACT_YEAR_VAR})
                      </label>
                      <InputText
                        value={adminForm.appendixSyncActYearVar || ''}
                        onChange={(e) => onChange('appendixSyncActYearVar', e.target.value)}
                        className="w-100"
                        placeholder="data-var ключ поля"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};
